import { useAuth } from '@frontegg/nextjs';
import {
  EmployeeDto,
  Role,
  UserDtoWithRoles,
  UserStatus,
} from '@zorro/clients';
import { FRONTEGG_ROLE_TO_ID_MAP, getQueryValue } from '@zorro/common-utils';
import { useRouter } from 'next/router';

import { useMonolithQuery } from './useMonolithQuery';

function extractFronteggUserRoles(
  roles: {
    id: string;
    key: string;
    name: string;
  }[]
): { id: Role; fronteggId: string }[] {
  return roles.map(({ id, key }) => ({
    id: FRONTEGG_ROLE_TO_ID_MAP[key],
    fronteggId: id,
  }));
}

export type TUseImpersonation = {
  sessionId: string;
  isLoading: boolean;
  isImpersonated: boolean;
  /**
   * The impersonated employee's id, read straight off the URL. Available on the
   * first render, unlike `employee`, which resolves through a fetch. Use this
   * when passing the subject to an API call. Undefined outside impersonation.
   */
  impersonatedEmployeeId?: string;
  user: UserDtoWithRoles | null;
  employee?: EmployeeDto | null;
  authUser: UserDtoWithRoles | null;
  authEmployee?: EmployeeDto | null;
  accessToken?: string;
};

export function useImpersonation(): TUseImpersonation {
  const router = useRouter();
  const {
    isAuthenticated,
    user: fronteggUser,
    isLoading: isLoadingFronteggUser,
  } = useAuth();

  const sessionId = fronteggUser?.sid ?? '';
  const frontEggUserRoles = extractFronteggUserRoles(fronteggUser?.roles ?? []);

  const { data: authUser, isLoading: isLoadingAuthUser } = useMonolithQuery({
    method: 'usersControllerFindUserByEmail',
    params: fronteggUser && [fronteggUser.email],
  });

  const { data: authEmployee, isLoading: isLoadingAuthEmployee } =
    useMonolithQuery({
      method: 'employeesControllerFindEmployeeByUserId',
      params: authUser && [authUser.id],
      enabled: frontEggUserRoles.some((role) => role.id === Role.EMPLOYEE),
    });

  const impersonatedEmployeeId = getQueryValue(
    router.query.impersonatedEmployeeId
  );

  const isImpersonated = Boolean(impersonatedEmployeeId);

  const employeeId = impersonatedEmployeeId ?? authEmployee?.id;

  const { data: employee, isLoading: isLoadingEmployee } = useMonolithQuery({
    method: 'employeesControllerFindOne',
    params: employeeId && [employeeId],
  });

  const { data: user, isLoading: isLoadingUser } = useMonolithQuery({
    method: 'usersControllerGetFronteggUser',
    params: employee && [employee.userId, employee.employerId],
  });

  const isLoading =
    isLoadingFronteggUser ||
    isLoadingAuthUser ||
    isLoadingAuthEmployee ||
    isLoadingUser ||
    isLoadingEmployee;

  if (isLoading || !isAuthenticated || !fronteggUser) {
    return {
      sessionId,
      isLoading,
      isImpersonated,
      impersonatedEmployeeId,
      user: null,
      employee: null,
      authUser: null,
      authEmployee: null,
    };
  }

  // 🧠 edge case: no user in the database (e.g. omnipotent admin)
  if (!authUser || !user) {
    const adoptedUser: UserDtoWithRoles = {
      id: fronteggUser.id,
      tenantId: fronteggUser.tenantId,
      email: fronteggUser.email,
      roles: extractFronteggUserRoles(fronteggUser.roles),
      firstName: fronteggUser.name,
      lastName: '',
      profilePictureUrl: fronteggUser.profilePictureUrl,
      phoneNumber: fronteggUser.phoneNumber,
      status: fronteggUser.activatedForTenant
        ? UserStatus.ACTIVATED
        : UserStatus.PENDING_LOGIN, // 🧠 no way to differentiate pending login and pending invitation in useAuth
    };

    return {
      sessionId,
      isLoading,
      isImpersonated,
      impersonatedEmployeeId,
      user: adoptedUser,
      employee: null,
      accessToken: fronteggUser.accessToken,
      authUser: adoptedUser,
      authEmployee: null,
    };
  }

  return {
    sessionId,
    isLoading,
    isImpersonated,
    impersonatedEmployeeId,
    user,
    employee,
    accessToken: fronteggUser.accessToken,
    authUser: {
      ...authUser,
      roles: extractFronteggUserRoles(fronteggUser.roles),
    },
    authEmployee,
  };
}
